import React from 'react';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import differenceInDays from 'date-fns/differenceInDays';
import { FormattedMessage } from 'react-intl';
import { RootState } from 'src/store';
import { IGuest, IHotelInfo, IRoom, IUrlRoom, IRoomsContent } from 'src/common-types/index';
import { BlueButton, EmptySearchMessage } from 'src/components/components/index';
import { roomsSearchActions, IRoomsSearchState, getRoomsDetails } from 'src/store/slices/index';
import { UpdateSvg, InfoGraySvg, CloseSvg, CalendarNotificationSvg } from 'src/assets/index';
import { DATES_LABEL, ROOMS_LABEL, ROOMS_SEARCH_LABEL } from 'src/constants/index';
import { UrlUtils, getNormalizedRooms, getDateCheckInOutISOString } from 'src/utils/index';
import { RoomsSkeleton } from '../rooms-skeleton';
import { Room } from '../room';
import { DatePickerComponent } from '../../search/date-picker';
import { RoomPickerComponent } from '../../search/room-picker';
import './style.scss';

interface IMapStateToProps {
  roomsSearchStore: IRoomsSearchState;
}

interface IMapDispatchToProps {
  setDates: (dates: { startDate: Date; endDate: Date }) => void;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  setKey: (key: string) => void;
  clearError: () => void;
  addEmptyRoom: () => void;
  updateAdultsCount: (roomId: string, count: number) => void;
  addKid: (roomId: string, kid: IGuest) => void;
  updateKidAge: (roomId: string, kidId: string, age: number) => void;
  removeRoom: (roomId: string) => void;
  removeKid: (roomId: string, kidId: string) => void;
  setRooms: (roms: IRoom[]) => void;
  setDatesString: (dates: { startDate: string; endDate: string }) => void;
  getRoomsDetails: (hotelId: number) => void;
  resetState: () => void;
  setShowUpdatedBanner: (show: boolean) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps {
  hotel: IHotelInfo;
  refAnchor: React.RefObject<HTMLDivElement>;
  matches?: boolean;
  isFromAdminPage?: boolean;
}

interface IState {
  isFirstRequestExecuted: boolean;
}

const zero = 0;

class RoomsSearchComponent extends React.Component<IProps, IState> {
  state: IState = {
    isFirstRequestExecuted: true,
  };

  constructor(props: IProps) {
    super(props);

    this.state.isFirstRequestExecuted = !props.isFromAdminPage;
  }

  updateSearch = () => {
    const { getRoomsDetails, hotel } = this.props;

    getRoomsDetails(hotel.hotelDetails.id);

    if (this.props.isFromAdminPage) {
      this.setState({ isFirstRequestExecuted: true });
    }
  };

  componentDidMount() {
    const { setDatesString, setRooms } = this.props;
    const values = UrlUtils.getValues();

    if (values[ROOMS_SEARCH_LABEL]) {
      const { startDate, endDate, rooms } = values[ROOMS_SEARCH_LABEL] as {
        startDate: string;
        endDate: string;
        rooms: IUrlRoom[];
      };

      setDatesString({ startDate, endDate } as { startDate: string; endDate: string });
      setRooms(getNormalizedRooms(rooms));
      this.updateSearch();
    } else {
      if (values[DATES_LABEL]) {
        setDatesString(values[DATES_LABEL] as { startDate: string; endDate: string });
      }

      if (values[ROOMS_LABEL]) {
        setRooms(getNormalizedRooms(values[ROOMS_LABEL] as Array<IUrlRoom>));
      }
    }
  }

  componentWillUnmount() {
    const { roomsSearchStore, resetState } = this.props;
    const { timer } = roomsSearchStore;

    if (timer) {
      clearTimeout(timer);
    }

    resetState();
  }

  getHotelInfo = (): IHotelInfo => {
    const { hotel, roomsSearchStore } = this.props;

    return roomsSearchStore.hotel ? roomsSearchStore.hotel : hotel;
  };

  getRoomContent = (room: string): IRoomsContent => {
    const { roomsContent = [] } = this.getHotelInfo();

    const content = roomsContent.find(({ roomKey }) => {
      return roomKey === room;
    });

    return content
      ? content
      : {
          amenities: [],
          descriptions: [],
          images: [],
          roomKey: room,
        };
  };

  render(): React.ReactNode {
    const {
      updateAdultsCount,
      removeRoom,
      removeKid,
      addKid,
      addEmptyRoom,
      updateKidAge,
      clearError,
      setDates,
      setStartDate,
      setEndDate,
      setKey,
      roomsSearchStore,
      refAnchor,
      matches,
      hotel,
      setShowUpdatedBanner,
    } = this.props;
    const { rooms, endDate, error, key, startDate, isRoomsLoading, showUpdatedBanner } =
      roomsSearchStore;
    const { adjustedPackageGroups } = this.getHotelInfo();
    const nights = differenceInDays(new Date(endDate), new Date(startDate));

    return (
      <div className="rooms-search" ref={refAnchor}>
        <div className="rooms-search__header">
          <FormattedMessage id="rooms.availability" />
        </div>
        <div className="rooms-search__search">
          <DatePickerComponent
            clearError={clearError}
            datesStore={{
              endDate,
              startDate,
              error,
              key,
            }}
            setDates={setDates}
            setEndDate={setEndDate}
            setKey={setKey}
            setStartDate={setStartDate}
            isRoomsSearch={matches}
            resetDates={() => void zero}
          />
          <RoomPickerComponent
            updateAdultsCount={updateAdultsCount}
            removeKid={removeKid}
            addKid={addKid}
            addEmptyRoom={addEmptyRoom}
            removeRoom={removeRoom}
            roomsStore={{ rooms }}
            updateKidAge={updateKidAge}
          />
          <div className="rooms-search__button">
            <BlueButton disabled={!startDate || !endDate} onClick={this.updateSearch}>
              <UpdateSvg />
              <FormattedMessage id="update.search.your.hotel" />
            </BlueButton>
          </div>
        </div>
        <div className="rooms-search__list">
          {isRoomsLoading ? (
            <div className="rooms-search__list-skeleton">
              <RoomsSkeleton />
              <RoomsSkeleton />
              <RoomsSkeleton />
            </div>
          ) : (
            <>
              {showUpdatedBanner && adjustedPackageGroups.length ? (
                <div className="rooms-search__updated-banner">
                  <div className="rooms-search__banner-icon">
                    <InfoGraySvg />
                  </div>
                  <div className="rooms-search__updated-text-wrapper">
                    <div className="rooms-search__updated-header">
                      <FormattedMessage id="rooms.updated" />
                    </div>
                    <div className="rooms-search__updated-text">
                      <FormattedMessage id="last.session.expired" />
                    </div>
                  </div>
                  <div
                    className="rooms-search__updated-close"
                    onClick={() => setShowUpdatedBanner(false)}
                  >
                    <CloseSvg />
                  </div>
                </div>
              ) : null}
              {!this.state.isFirstRequestExecuted && (
                <div className="rooms-search__specify-travel-banner">
                  <div className="rooms-search__banner-icon">
                    <CalendarNotificationSvg />
                  </div>
                  <div className="rooms-search__specify-travel-text-wrapper">
                    <div className="rooms-search__specify-travel-text">
                      <FormattedMessage id="specify.travel.dates" />
                    </div>
                  </div>
                </div>
              )}
              <div className="rooms-search__list-wrapper">
                {adjustedPackageGroups?.length ? (
                  adjustedPackageGroups.map(({ groupedPackages }) => {
                    const currentPackage = groupedPackages[zero];

                    if (!currentPackage || !this.state.isFirstRequestExecuted) {
                      return null;
                    }

                    return (
                      <Room
                        key={currentPackage.packageId}
                        nights={nights}
                        hotelId={hotel.hotelDetails.id}
                        groupedPackages={groupedPackages}
                        getRoomContent={this.getRoomContent}
                      />
                    );
                  })
                ) : (
                  <div className="rooms-search__list-wrapper-no-rooms">
                    <EmptySearchMessage label={<FormattedMessage id="no.available.rooms" />} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    roomsSearchStore: state.roomsSearchStore,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<RootState, undefined, Action>,
): IMapDispatchToProps => ({
  setDates: (dates: { startDate: Date; endDate: Date }) => {
    dispatch(
      roomsSearchActions.setDates({
        startDate: getDateCheckInOutISOString(dates.startDate),
        endDate: getDateCheckInOutISOString(dates.endDate),
      }),
    );
  },
  setDatesString: (dates: { startDate: string; endDate: string }) => {
    dispatch(
      roomsSearchActions.setDates({
        startDate: dates.startDate,
        endDate: dates.endDate,
      }),
    );
  },
  setStartDate: (date: Date) => {
    dispatch(roomsSearchActions.setStartDate(getDateCheckInOutISOString(date)));
  },
  setEndDate: (date: Date) => {
    dispatch(roomsSearchActions.setEndDate(getDateCheckInOutISOString(date)));
  },
  setKey: (key: string) => {
    dispatch(roomsSearchActions.setKey(key));
  },
  clearError: () => {
    dispatch(roomsSearchActions.setError(''));
  },
  addEmptyRoom: () => {
    dispatch(roomsSearchActions.addEmptyRoom());
  },
  updateAdultsCount: (roomId: string, count: number) => {
    dispatch(roomsSearchActions.updateAdultsCount({ roomId, count }));
  },
  addKid: (roomId: string, kid: IGuest) => {
    dispatch(roomsSearchActions.addKid({ roomId, kid }));
  },
  updateKidAge: (roomId: string, kidId: string, age: number) => {
    dispatch(roomsSearchActions.updateKidAge({ roomId, kidId, age }));
  },
  removeRoom: (roomId: string) => {
    dispatch(roomsSearchActions.removeRoom(roomId));
  },
  removeKid: (roomId: string, kidId: string) => {
    dispatch(roomsSearchActions.removeKid({ roomId, kidId }));
  },
  setRooms: (rooms: IRoom[]) => {
    dispatch(roomsSearchActions.setRooms(rooms));
  },
  getRoomsDetails: (hotelId: number) => {
    dispatch(getRoomsDetails(hotelId));
  },
  setShowUpdatedBanner: (show: boolean) => {
    dispatch(roomsSearchActions.setShowUpdatedBanner(show));
  },
  resetState: () => {
    dispatch(roomsSearchActions.resetState());
  },
});

export const RoomsSearch = connect(mapStateToProps, mapDispatchToProps)(RoomsSearchComponent);

